// Firebase Authentication Error Handler
// This utility maps Firebase authentication error codes to user-friendly messages

/**
 * Interface for Firebase Authentication Error
 */
interface FirebaseAuthError extends Error {
	code: string
	message: string
}

/**
 * Maps Firebase authentication error codes to user-friendly messages
 * @param {FirebaseAuthError} error - The error object thrown by Firebase
 * @returns {string} A user-friendly error message
 */
export const handleFirebaseAuthError = (error: FirebaseAuthError): string => {
	const errorCode = error.code
	const errorMessage = error.message

	// Log the original error for debugging
	console.error('Firebase Auth Error:', errorCode, errorMessage)

	// Map common Firebase auth error codes to user-friendly messages
	switch (errorCode) {
		case 'auth/invalid-email':
			return 'The email address is not valid.'
		case 'auth/user-disabled':
			return 'This account has been disabled. Please contact support.'
		case 'auth/user-not-found':
			return 'No account found with this email. Please check your email or sign up.'
		case 'auth/invalid-credential':
			return 'Incorrect password. Please try again or reset your password.'
		case 'auth/email-already-in-use':
			return 'An account with this email already exists.'
		case 'auth/weak-password':
			return 'Password is too weak. Please use a stronger password.'
		case 'auth/operation-not-allowed':
			return 'This operation is not allowed. Please contact support.'
		case 'auth/account-exists-with-different-credential':
			return 'An account already exists with the same email but different sign-in credentials.'
		case 'auth/network-request-failed':
			return 'Network error. Please check your internet connection and try again.'
		case 'auth/too-many-requests':
			return 'Too many unsuccessful login attempts. Please try again later or reset your password.'
		case 'auth/popup-closed-by-user':
			return 'The sign-in popup was closed before completing the sign-in.'
		default:
			return 'An error occurred during authentication. Please try again later.'
	}
}
